import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  MainContent,
  Card,
  Heading1,
  Heading2,
  Text,
  Grid,
  FlexColumn,
  FlexRow,
  SectionTitle,
  ContentSection,
  Button,
  Input,
} from '../../styles/StyledComponents';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { useAuth } from '../../contexts/AuthContext';
import { ActivityIndicator } from '../../components/common';
import { ProfileService } from '../../services/ProfileService';
import { Mail, User, Shield, Bell, Globe, Phone, Edit2, Save, X, Lock, Eye, EyeOff } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const PageWrapper = styled.div`
  padding: 0 ${Spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl};
  margin-top: calc(${Spacing.xxl} + 30px);

  @media (min-width: 1025px) {
    padding: 0 calc(${Spacing.xl} + 30px);
  }

  @media (max-width: 768px) {
    padding: 0 ${Spacing.md};
    margin-top: calc(${Spacing.xl} + 30px);
  }
`;

const HeroCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg};
  padding: ${Spacing.xxl};
  background: linear-gradient(135deg, ${ThemeColors.primary.light}, ${ThemeColors.primary.main}33);

  @media (max-width: 768px) {
    padding: ${Spacing.lg};
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xl};
  flex-wrap: wrap;
`;

const Avatar = styled.div`
  width: 96px;
  height: 96px;
  border-radius: ${BorderRadius.round};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: white;
  box-shadow: ${Shadows.md};
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const InfoGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const InfoItem = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.md};
  padding: ${Spacing.lg};
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.round};
  background-color: ${ThemeColors.primary.light};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ThemeColors.primary.dark};
`;

const ErrorState = styled(Card)`
  padding: ${Spacing.xl};
  background-color: ${ThemeColors.error.light};
  border: 1px solid ${ThemeColors.error.main};
  color: ${ThemeColors.error.dark};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: ${ThemeColors.primary.main};
  cursor: pointer;
  padding: ${Spacing.xs};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    color: ${ThemeColors.primary.dark};
    text-decoration: underline;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.sm};
`;

const FormInput = styled(Input)`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const PasswordInput = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${Spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  padding: ${Spacing.xs};

  &:hover {
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.xs};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${Spacing.xs} ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;

    &:hover {
      background: ${ThemeColors.primary.dark};
    }
  ` : `
    background: ${ThemeColors.gray[100]};
    color: ${ThemeColors.text.primary};

    &:hover {
      background: ${ThemeColors.gray[200]};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  flex: 1;
`;

const InfoItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

interface FirestoreUserProfile {
  email?: string;
  username?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  language?: string;
  lastLogin?: string;
  notificationsEnabled?: boolean;
}

export const ProfileScreen: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [profileData, setProfileData] = useState<FirestoreUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Edit states
  const [editingName, setEditingName] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  
  // Form values
  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Loading states
  const [updatingName, setUpdatingName] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const profileRef = doc(db, 'users', user.id);
        const snapshot = await getDoc(profileRef);

        if (snapshot.exists()) {
          const data = snapshot.data() as FirestoreUserProfile;
          setProfileData(data);
        } else {
          setProfileData(null);
        }
      } catch (err: any) {
        console.error('Failed to load profile:', err);
        setError(err?.message || t('profileScreen.loadError', 'Unable to load profile information.'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id, t]);

  // Computed values - must be defined before useEffect hooks that use them
  const firstName = profileData?.firstName || user?.firstName || '';
  const lastName = profileData?.lastName || user?.lastName || 'mckenzie';
  
  const initials = useMemo(() => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    if (lastName) {
      return lastName.slice(0, 2).toUpperCase();
    }
    const fallbackName = profileData?.fullName || user?.username || user?.email || '';
    if (!fallbackName) {
      return 'NA';
    }
    const parts = fallbackName.split(/[\s._-]+/).filter(Boolean);
    if (!parts.length) {
      return fallbackName.slice(0, 2).toUpperCase();
    }
    return parts
      .map(part => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }, [firstName, lastName, profileData?.fullName, user?.username, user?.email]);

  const displayName = useMemo(() => {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (profileData?.fullName) return profileData.fullName;
    if (user?.username) return user.username;
    if (user?.email) return user.email;
    return t('profileScreen.notAvailable', 'Not available');
  }, [firstName, lastName, profileData?.fullName, user?.username, user?.email, t]);

  const contactEmail = profileData?.email || user?.email || t('profileScreen.notProvided', 'Not provided');
  const contactPhone = profileData?.phone || t('profileScreen.notProvided', 'Not provided');
  const preferredLanguage = profileData?.language || t('profileScreen.automatic', 'Automatic');
  const lastLogin = profileData?.lastLogin || t('profileScreen.notAvailable', 'Not available');

  // Initialize form values when editing starts
  useEffect(() => {
    if (editingName) {
      setFirstNameValue(firstName);
      setLastNameValue(lastName);
    }
  }, [editingName, firstName, lastName]);

  useEffect(() => {
    if (editingEmail) {
      setEmailValue(contactEmail);
    }
  }, [editingEmail, contactEmail]);

  // Handlers
  const handleUpdateName = async () => {
    if (!firstNameValue.trim() || !lastNameValue.trim() || !user?.id) return;
    
    setUpdatingName(true);
    try {
      const result = await ProfileService.updateName(
        user.id, 
        firstNameValue.trim(), 
        lastNameValue.trim()
      );
      if (result.success) {
        toast.success(t('profileScreen.nameUpdated', 'Name updated successfully'));
        setEditingName(false);
        // Reload profile data
        const profileRef = doc(db, 'users', user.id);
        const snapshot = await getDoc(profileRef);
        if (snapshot.exists()) {
          setProfileData(snapshot.data() as FirestoreUserProfile);
        }
      } else {
        toast.error(result.error || t('profileScreen.updateError', 'Failed to update name'));
      }
    } catch (err: any) {
      toast.error(err.message || t('profileScreen.updateError', 'Failed to update name'));
    } finally {
      setUpdatingName(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!emailValue.trim() || !currentPassword || !user?.id) return;
    
    if (!emailValue.includes('@')) {
      toast.error(t('profileScreen.invalidEmail', 'Please enter a valid email address'));
      return;
    }
    
    setUpdatingEmail(true);
    try {
      const result = await ProfileService.updateEmail(user.id, emailValue.trim(), currentPassword);
      if (result.success) {
        toast.success(t('profileScreen.emailUpdated', 'Email updated successfully'));
        setEditingEmail(false);
        setCurrentPassword('');
        // Reload profile data
        const profileRef = doc(db, 'users', user.id);
        const snapshot = await getDoc(profileRef);
        if (snapshot.exists()) {
          setProfileData(snapshot.data() as FirestoreUserProfile);
        }
      } else {
        toast.error(result.error || t('profileScreen.updateError', 'Failed to update email'));
      }
    } catch (err: any) {
      toast.error(err.message || t('profileScreen.updateError', 'Failed to update email'));
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) return;
    
    if (newPassword.length < 6) {
      toast.error(t('profileScreen.passwordTooShort', 'Password must be at least 6 characters'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('profileScreen.passwordsDontMatch', 'New passwords do not match'));
      return;
    }
    
    setUpdatingPassword(true);
    try {
      const result = await ProfileService.updatePassword(currentPassword, newPassword);
      if (result.success) {
        toast.success(t('profileScreen.passwordUpdated', 'Password updated successfully'));
        setEditingPassword(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(result.error || t('profileScreen.updateError', 'Failed to update password'));
      }
    } catch (err: any) {
      toast.error(err.message || t('profileScreen.updateError', 'Failed to update password'));
    } finally {
      setUpdatingPassword(false);
    }
  };

  return (
    <MainContent>
      <Toaster position="top-right" />
      <PageWrapper>
        <HeroCard>
          <HeroContent>
            <Avatar aria-hidden="true">{initials}</Avatar>
            <HeroText>
              <Heading1>{t('profileScreen.title', 'My Profile')}</Heading1>
              <Text size={Typography.fontSize.md} color={ThemeColors.text.secondary}>
                {t('profileScreen.subtitle', 'Review your account details and preferences.')}
              </Text>
            </HeroText>
          </HeroContent>
        </HeroCard>

        {loading ? (
          <Card>
            <FlexRow style={{ alignItems: 'center', gap: Spacing.md, padding: Spacing.lg }}>
              <ActivityIndicator size="small" />
              <Text>{t('profileScreen.loading', 'Loading your profile...')}</Text>
            </FlexRow>
          </Card>
        ) : error ? (
          <ErrorState role="alert">
            <Heading2 style={{ margin: 0 }}>{t('profileScreen.errorTitle', 'Unable to load profile')}</Heading2>
            <Text>{error}</Text>
          </ErrorState>
        ) : (
          <>
            <ContentSection>
              <SectionTitle>{t('profileScreen.contactInformation', 'Contact Information')}</SectionTitle>
              <InfoGrid gap={Spacing.md}>
                <InfoItem>
                  <InfoIcon>
                    <User size={20} />
                  </InfoIcon>
                  <InfoItemContent>
                    <InfoItemHeader>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.fullName', 'Full Name')}
                    </Text>
                      {!editingName && (
                        <EditButton onClick={() => setEditingName(true)}>
                          <Edit2 size={16} />
                          {t('profileScreen.edit', 'Edit')}
                        </EditButton>
                      )}
                    </InfoItemHeader>
                    {editingName ? (
                      <EditForm>
                        <FormInput
                          type="text"
                          value={firstNameValue}
                          onChange={(e) => setFirstNameValue(e.target.value)}
                          placeholder={t('profileScreen.enterFirstName', 'First Name')}
                          disabled={updatingName}
                        />
                        <FormInput
                          type="text"
                          value={lastNameValue}
                          onChange={(e) => setLastNameValue(e.target.value)}
                          placeholder={t('profileScreen.enterLastName', 'Last Name')}
                          disabled={updatingName}
                        />
                        <FormActions>
                          <ActionButton
                            variant="primary"
                            onClick={handleUpdateName}
                            disabled={updatingName || !firstNameValue.trim() || !lastNameValue.trim()}
                          >
                            <Save size={16} />
                            {updatingName ? t('profileScreen.saving', 'Saving...') : t('profileScreen.save', 'Save')}
                          </ActionButton>
                          <ActionButton
                            variant="secondary"
                            onClick={() => {
                              setEditingName(false);
                              setFirstNameValue('');
                              setLastNameValue('');
                            }}
                            disabled={updatingName}
                          >
                            <X size={16} />
                            {t('profileScreen.cancel', 'Cancel')}
                          </ActionButton>
                        </FormActions>
                      </EditForm>
                    ) : (
                    <Text>{displayName}</Text>
                    )}
                  </InfoItemContent>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>
                    <Mail size={20} />
                  </InfoIcon>
                  <InfoItemContent>
                    <InfoItemHeader>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.email', 'Email')}
                    </Text>
                      {!editingEmail && (
                        <EditButton onClick={() => setEditingEmail(true)}>
                          <Edit2 size={16} />
                          {t('profileScreen.edit', 'Edit')}
                        </EditButton>
                      )}
                    </InfoItemHeader>
                    {editingEmail ? (
                      <EditForm>
                        <FormInput
                          type="email"
                          value={emailValue}
                          onChange={(e) => setEmailValue(e.target.value)}
                          placeholder={t('profileScreen.enterEmail', 'Enter your email')}
                          disabled={updatingEmail}
                        />
                        <FormInput
                          type={showPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder={t('profileScreen.currentPassword', 'Current password (required)')}
                          disabled={updatingEmail}
                        />
                        <FormActions>
                          <ActionButton
                            variant="primary"
                            onClick={handleUpdateEmail}
                            disabled={updatingEmail || !emailValue.trim() || !currentPassword}
                          >
                            <Save size={16} />
                            {updatingEmail ? t('profileScreen.saving', 'Saving...') : t('profileScreen.save', 'Save')}
                          </ActionButton>
                          <ActionButton
                            variant="secondary"
                            onClick={() => {
                              setEditingEmail(false);
                              setEmailValue('');
                              setCurrentPassword('');
                            }}
                            disabled={updatingEmail}
                          >
                            <X size={16} />
                            {t('profileScreen.cancel', 'Cancel')}
                          </ActionButton>
                        </FormActions>
                      </EditForm>
                    ) : (
                    <Text>{contactEmail}</Text>
                    )}
                  </InfoItemContent>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>
                    <Phone size={20} />
                  </InfoIcon>
                  <FlexColumn style={{ gap: Spacing.xs }}>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.phone', 'Phone')}
                    </Text>
                    <Text>{contactPhone}</Text>
                  </FlexColumn>
                </InfoItem>
              </InfoGrid>
            </ContentSection>

            <ContentSection>
              <SectionTitle>{t('profileScreen.accountDetails', 'Account Details')}</SectionTitle>
              <InfoGrid gap={Spacing.md}>
                <InfoItem>
                  <InfoIcon>
                    <Lock size={20} />
                  </InfoIcon>
                  <InfoItemContent>
                    <InfoItemHeader>
                      <Text weight={Typography.fontWeight.semiBold}>
                        {t('profileScreen.password', 'Password')}
                      </Text>
                      {!editingPassword && (
                        <EditButton onClick={() => setEditingPassword(true)}>
                          <Edit2 size={16} />
                          {t('profileScreen.changePassword', 'Change Password')}
                        </EditButton>
                      )}
                    </InfoItemHeader>
                    {editingPassword ? (
                      <EditForm>
                        <PasswordInput>
                          <FormInput
                            type={showPassword ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder={t('profileScreen.currentPassword', 'Current password')}
                            disabled={updatingPassword}
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={updatingPassword}
                          >
                            {showPassword ? <EyeOff /> : <Eye />}
                          </PasswordToggle>
                        </PasswordInput>
                        <PasswordInput>
                          <FormInput
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder={t('profileScreen.newPassword', 'New password')}
                            disabled={updatingPassword}
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            disabled={updatingPassword}
                          >
                            {showNewPassword ? <EyeOff /> : <Eye />}
                          </PasswordToggle>
                        </PasswordInput>
                        <PasswordInput>
                          <FormInput
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder={t('profileScreen.confirmPassword', 'Confirm new password')}
                            disabled={updatingPassword}
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={updatingPassword}
                          >
                            {showConfirmPassword ? <EyeOff /> : <Eye />}
                          </PasswordToggle>
                        </PasswordInput>
                        <FormActions>
                          <ActionButton
                            variant="primary"
                            onClick={handleUpdatePassword}
                            disabled={updatingPassword || !currentPassword || !newPassword || !confirmPassword}
                          >
                            <Save size={16} />
                            {updatingPassword ? t('profileScreen.saving', 'Saving...') : t('profileScreen.save', 'Save')}
                          </ActionButton>
                          <ActionButton
                            variant="secondary"
                            onClick={() => {
                              setEditingPassword(false);
                              setCurrentPassword('');
                              setNewPassword('');
                              setConfirmPassword('');
                            }}
                            disabled={updatingPassword}
                          >
                            <X size={16} />
                            {t('profileScreen.cancel', 'Cancel')}
                          </ActionButton>
                        </FormActions>
                      </EditForm>
                    ) : (
                      <Text>••••••••</Text>
                    )}
                  </InfoItemContent>
                </InfoItem>
              </InfoGrid>
              <InfoGrid gap={Spacing.md} style={{ marginTop: Spacing.md }}>
                <InfoItem>
                  <InfoIcon>
                    <Shield size={20} />
                  </InfoIcon>
                  <FlexColumn style={{ gap: Spacing.xs }}>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.role', 'Role')}
                    </Text>
                    <Text>{user?.role || t('profileScreen.notAvailable', 'Not available')}</Text>
                  </FlexColumn>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>
                    <User size={20} />
                  </InfoIcon>
                  <FlexColumn style={{ gap: Spacing.xs }}>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.username', 'Username')}
                    </Text>
                    <Text>{user?.username || t('profileScreen.notProvided', 'Not provided')}</Text>
                  </FlexColumn>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>
                    <Globe size={20} />
                  </InfoIcon>
                  <FlexColumn style={{ gap: Spacing.xs }}>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.language', 'Preferred Language')}
                    </Text>
                    <Text>{preferredLanguage}</Text>
                  </FlexColumn>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>
                    <Bell size={20} />
                  </InfoIcon>
                  <FlexColumn style={{ gap: Spacing.xs }}>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.notifications', 'Notifications')}
                    </Text>
                    <Text>
                      {profileData?.notificationsEnabled
                        ? t('profileScreen.notificationsOn', 'Enabled')
                        : t('profileScreen.notificationsOff', 'Disabled')}
                    </Text>
                  </FlexColumn>
                </InfoItem>
                <InfoItem>
                  <InfoIcon>
                    <Shield size={20} />
                  </InfoIcon>
                  <FlexColumn style={{ gap: Spacing.xs }}>
                    <Text weight={Typography.fontWeight.semiBold}>
                      {t('profileScreen.lastLogin', 'Last Login')}
                    </Text>
                    <Text>{lastLogin}</Text>
                  </FlexColumn>
                </InfoItem>
              </InfoGrid>
            </ContentSection>
          </>
        )}
      </PageWrapper>
    </MainContent>
  );
};

export default ProfileScreen;

